<ng-container [formGroup]="form">
  <div class="d-flex flex-column gap-3">
    <ul
      ngbNav
      #nav="ngbNav"
      [activeId]="activeTab"
      (activeIdChange)="onActiveTabChange($event)"
      class="nav-tabs">
      @if (featureFlags.topicInput) {
        <li [ngbNavItem]="InputType.Topic">
          @if (!onlyOneTab()) {
            <button ngbNavLink>Specify Subject</button>
          }
          <ng-template ngbNavContent>
            <div class="d-flex flex-column gap-3" formGroupName="topic">
              <div>
                <label for="topic" class="form-label required">Subject</label>
                <input
                  type="text"
                  class="form-control"
                  id="topic"
                  aria-label="Subject"
                  formControlName="name" />
                <div
                  class="text-end mt-1 text-body-secondary"
                  style="height: 1.5rem; margin-bottom: -1.2rem">
                  {{
                    form.get('topic.name').value?.length
                      ? form.get('topic.name').value?.length +
                        ' character' +
                        (form.get('topic.name').value?.length > 1 ? 's' : '')
                      : ' '
                  }}
                </div>

                @if (
                  form.get('topic.name')?.invalid &&
                  form.get('topic.name')?.touched
                ) {
                  <div class="form-feedback text-primary">
                    Please add a subject.
                  </div>
                }
              </div>
              <div class="d-flex flex-column gap-2">
                @if (
                  !isBrainstorming &&
                  !form.get('topic.branstormConcepts').length
                ) {
                  <div>
                    <button
                      type="button"
                      class="btn btn-secondary btn-sm"
                      (click)="onBrainstormConcepts()"
                      [disabled]="
                        !form.get('topic.name').value || isBrainstorming
                      ">
                      Brainstorm concepts
                    </button>
                  </div>
                }
                @if (isBrainstorming) {
                  <div
                    class="d-flex justify-content-center align-items-center gap-2">
                    <div class="spinner-grow text-primary" role="status">
                      <span class="visually-hidden">
                        Brainstorming concepts...
                      </span>
                    </div>
                    <div>Brainstorming concepts...</div>
                  </div>
                }
                @if (
                  !isBrainstorming && form.get('topic.branstormConcepts').length
                ) {
                  <div
                    class="d-flex justify-content-between align-items-center">
                    <label class="form-label" for>
                      You can choose brainstormed concepts
                    </label>
                    <button
                      type="button"
                      class="btn btn-secondary btn-sm"
                      (click)="regenerateBrainstormConcepts()"
                      [disabled]="isBrainstorming">
                      Brainstorm harder
                    </button>
                  </div>
                  <div class="list-group" formArrayName="branstormConcepts">
                    @for (item of currentBrainstormConcepts; track $index) {
                      <label
                        class="list-group-item list-group-item-action cursor-pointer"
                        [formGroupName]="item.index"
                        [class.active]="item.control.get('selected').value">
                        <input
                          hidden
                          type="checkbox"
                          formControlName="selected" />
                        {{ item.control.get('concept').value }}
                      </label>
                    }
                  </div>
                  @if (previousBrainstormConcepts.length) {
                    <div formArrayName="branstormConcepts" ngbAccordion>
                      <div ngbAccordionItem [collapsed]="true">
                        <h2 ngbAccordionHeader>
                          <button ngbAccordionButton>
                            Previously generated concepts:
                          </button>
                        </h2>
                        <div ngbAccordionCollapse>
                          <div class="list-group p-0" ngbAccordionBody>
                            <ng-template>
                              @for (
                                item of previousBrainstormConcepts;
                                track $index
                              ) {
                                <label
                                  class="list-group-item list-group-item-action cursor-pointer"
                                  [formGroupName]="item.index"
                                  [class.active]="
                                    item.control.get('selected').value
                                  ">
                                  <input
                                    hidden
                                    type="checkbox"
                                    formControlName="selected" />
                                  {{ item.control.get('concept').value }}
                                </label>
                              }
                            </ng-template>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                }
              </div>

              <ng-container
                [ngTemplateOutlet]="commonInputParams"></ng-container>
            </div>
          </ng-template>
        </li>
      }
      @if (featureFlags.textInput) {
        <li [ngbNavItem]="InputType.Text">
          @if (!onlyOneTab()) {
            <button ngbNavLink>
              {{ featureFlags.topicInput ? 'Or ' : '' }}Provide Input Text
            </button>
          }
          <ng-template ngbNavContent>
            <div class="d-flex flex-column gap-3" formGroupName="text">
              <div>
                <label for="mainSourceText" class="form-label required">
                  Enter text as main source
                </label>
                <textarea
                  class="form-control"
                  id="mainSourceText"
                  formControlName="mainText"
                  style="height: 300px"></textarea>
                @if (
                  form.get('text.mainText').invalid &&
                  form.get('text.mainText').touched
                ) {
                  <div class="form-feedback text-primary">
                    Please add a text.
                  </div>
                }
              </div>

              <ng-container
                [ngTemplateOutlet]="commonInputParams"></ng-container>
            </div>
          </ng-template>
        </li>
      }
      @if (featureFlags.sourcesInput) {
        <li [ngbNavItem]="InputType.Sources">
          @if (!onlyOneTab()) {
            <button ngbNavLink>
              {{
                featureFlags.topicInput || featureFlags.textInput ? 'Or ' : ''
              }}Specify Sources To Be Used
            </button>
          }
          <ng-template ngbNavContent>
            <div class="row" formGroupName="sources">
              <div class="col d-flex flex-column">
                <label for class="form-label">
                  Enter up to {{ maxSourceItems }} source URLs or texts
                </label>
                <div class="col d-flex flex-column gap-2" formArrayName="items">
                  @for (
                    item of form.get('sources.items').controls;
                    track $index;
                    let idx = $index
                  ) {
                    <div
                      class="d-flex gap-2 p-2 border rounded bg-light-subtle">
                      <textarea
                        class="form-control"
                        placeholder="Enter source URL or text"
                        [formControlName]="idx"
                        cdkTextareaAutosize
                        cdkAutosizeMinRows="1"
                        cdkAutosizeMaxRows="10"></textarea>
                      <div class="mt-1">
                        @if (form.get('sources.items').controls.length > 1) {
                          <button
                            class="btn btn-secondary btn-sm"
                            (click)="removeSource(idx)">
                            <i class="fa fa-trash"></i>
                          </button>
                        }
                      </div>
                    </div>
                  }
                  <div class="d-flex justify-content-end">
                    @if (
                      form.get('sources.items').controls.length < maxSourceItems
                    ) {
                      <button class="btn btn-secondary" (click)="addSource()">
                        + Add source
                      </button>
                    }
                  </div>
                  @if (
                    form.get('sources.items')?.invalid &&
                    form.get('sources.items')?.touched
                  ) {
                    <div class="form-feedback text-primary">
                      Please add at least one source.
                    </div>
                  }
                </div>
              </div>
              <div class="col d-flex flex-column gap-3">
                <div>
                  <label for="language" class="form-label">
                    Desired output language
                  </label>
                  <select class="form-select" formControlName="language">
                    <option value="de">German</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div>
                  <label for="style" class="form-label">Desired style</label>
                  <select class="form-select" formControlName="style">
                    @for (item of contentStylesOptions; track item.value) {
                      <option [value]="item.value">
                        {{ item.label }}
                      </option>
                    }
                  </select>
                </div>
                <div>
                  <label for="language" class="form-label">
                    Context or additional instructions
                  </label>
                  <textarea
                    class="form-control"
                    [placeholder]="
                      form.get('sources.language')!.value === 'en'
                        ? 'e.g. Please focus on Source 1, while mentioning some details from the other sources.'
                        : 'z.B. Bitte konzentriere dich auf Quelle 1 und nenne einige Details aus den anderen Quellen.'
                    "
                    formControlName="context"></textarea>
                </div>
              </div>
            </div>
          </ng-template>
        </li>
      }
    </ul>
    <div [ngbNavOutlet]="nav"></div>
  </div>
</ng-container>

<ng-template #commonInputParams>
  <ng-container [formGroup]="form">
    <div>
      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          role="switch"
          id="provideContext"
          formControlName="provideContext" />
        <label class="form-check-label" for="provideContext">
          Provide context for the subject
        </label>
      </div>
      @if (form.get('provideContext').value) {
        <div>
          <label for="context" class="form-label">Context</label>
          <textarea
            class="form-control"
            formControlName="context"
            id="context"></textarea>
        </div>
      }
    </div>
    @if (!hidePodcastTitle()) {
      <div>
        <label for="podcastName" class="form-label required">
          Podcast Title
        </label>
        <input
          type="text"
          class="form-control"
          id="podcastName"
          aria-label="Podcast Title"
          formControlName="podcastName" />
        @if (
          form.get('podcastName').invalid && form.get('podcastName').touched
        ) {
          <div class="form-feedback text-primary">
            Please add a podcast title.
          </div>
        }
      </div>
    }
    <div ngbAccordion>
      <div ngbAccordionItem>
        <h2 ngbAccordionHeader>
          <button ngbAccordionButton>Additional settings</button>
        </h2>
        <div ngbAccordionCollapse>
          <div ngbAccordionBody>
            <ng-template>
              <div class="d-flex flex-column gap-3">
                <div class="row">
                  <div class="col">
                    <label for="name" class="form-label">
                      How many guests should be considered?
                    </label>
                    <select
                      class="form-select"
                      aria-label="How many guests should be considered for the script?"
                      formControlName="guests">
                      <option value="0">Only narrator</option>
                      <option value="1">1 Guest</option>
                    </select>
                  </div>
                  <div class="col">
                    <label for="name" class="form-label">
                      How many sections should be considered?
                    </label>
                    <select
                      class="form-select"
                      aria-label="How many sections should be considered for the outline?"
                      formControlName="sections">
                      <option value="4">4 Sections</option>
                      <option value="5">5 Sections</option>
                      <option value="6">6 Sections</option>
                      <option value="6">7 Sections</option>
                      <option value="6">8 Sections</option>
                      <option value="6">9 Sections</option>
                      <option value="6">10 Sections</option>
                      <option value="6">11 Sections</option>
                      <option value="6">12 Sections</option>
                    </select>
                  </div>
                </div>
              </div>
            </ng-template>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
</ng-template>
